<template>
  <a-dropdown
    v-model:open="isEditing"
    :trigger="['click']"
    placement="rightTop"
  >
    <div
      class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
      @click.stop="handleEdit"
    >
      <ThemeImage
        :icon="imageNames.EDIT"
        alt="edit_icon"
        class="size-5"
      />
      <span class="text-sm text_color">Edit property</span>
      <ThemeImage
        :icon="imageNames.RIGHT_ARROW"
        alt="arrow_right"
        class="size-3 ml-auto"
      />
    </div>
    <template #overlay>
      <div class="record_type_edit_dropdown_content">
        <div class="flex flex-col w-[320px] p-4" @click.stop>
          <SkeletonLoader v-if="isLoading" page="manage_columns" />

          <div v-else>
            <div class="mb-3">
              <span class="text-xs secondary_color mb-2 block">CHOOSE OBJECTS</span>
              <ObjectSelector
                ref="objectSelectorRef"
                v-model="selectedRelatedObjects"
                :exclude-slug="entityType"
                :auto-fetch="true"
                :include-standard-objects="true"
              />
            </div>

            <div class="mb-3">
              <span class="text-xs secondary_color mb-2 inline-flex items-center gap-1">
                RELATIONSHIP TYPE
                <a-tooltip placement="top">
                  <template #title>
                    <div class="text-xs">
                      <div class="mb-1"><b>Lookup</b> — soft reference. Deleting the parent leaves this record intact.</div>
                      <div><b>Master–Detail</b> — hard reference. Deleting the parent <b>cascades</b> and removes this record too.</div>
                    </div>
                  </template>
                  <ThemeImage
                    :icon="imageNames.INFO_GREY"
                    class="size-3 cursor-help opacity-70"
                    alt="info"
                  />
                </a-tooltip>
              </span>
              <a-select
                v-model:value="relationshipMode"
                class="w-full"
                :options="RELATIONSHIP_MODE_OPTIONS"
              />
            </div>

            <div class="flex justify-end gap-2 mt-4">
              <a-button class="secondary_medium" @click.stop="exitEditMode">
                Cancel
              </a-button>
              <a-button
                class="primary_medium"
                :loading="isSaving"
                :disabled="selectedRelatedObjects.length === 0"
                @click.stop="save"
              >
                Save
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { ref } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ObjectSelector from "@/src/components/record_type_components/ObjectSelector.vue";
import CRMService from "@/src/api/CRMService.js";
import {
  ATTRIBUTE_TYPES,
  normalizeRelationshipMode,
  RELATIONSHIP_MODE_OPTIONS,
} from "@/src/constants/crmConstants.js";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps({
  propertyMeta: {
    type: Object,
    default: null,
  },
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
  isDynamicObject: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["property-updated", "close"]);

const isEditing = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const selectedRelatedObjects = ref([]);
const relationshipMode = ref("lookup");
const objectSelectorRef = ref(null);

const handleEdit = async () => {
  if (!props.isDynamicObject) {
    message.warning("Record type editing is only available for custom objects");
    return;
  }

  isEditing.value = true;
  await loadRelatedObjects();
};

const loadRelatedObjects = async () => {
  if (!props.isDynamicObject || props.propertyMeta?.datatype !== ATTRIBUTE_TYPES.RECORD) {
    return;
  }

  try {
    isLoading.value = true;

    // Seed the relationship-mode select from the current attribute.
    // Anything not explicitly master_detail (incl. missing on older attrs) → lookup.
    relationshipMode.value = normalizeRelationshipMode(
      props.propertyMeta?.relationship_mode,
    );

    // First check if related_objects is already available in propertyMeta
    const existingRelatedObjects = props.propertyMeta?.related_objects || [];

    if (existingRelatedObjects.length > 0) {
      let objects = store.getters["objects/objects"] || [];

      if (objects.length === 0) {
        await store.dispatch("objects/fetchObjects");
        objects = store.getters["objects/objects"] || [];
      }

      selectedRelatedObjects.value = existingRelatedObjects
        .map((ro) => {
          const obj = objects.find(
            (o) => o.slug === ro.object_type || o.id === ro.object_id
          );
          return obj?.id;
        })
        .filter(Boolean);
    } else {
      selectedRelatedObjects.value = [];
    }
  } catch (error) {
    console.error("Error loading related objects:", error);
    selectedRelatedObjects.value = [];
  } finally {
    isLoading.value = false;
  }
};

const exitEditMode = () => {
  isEditing.value = false;
  selectedRelatedObjects.value = [];
};

const refreshSchemaStore = async () => {
  await store.dispatch("schema/refreshSchema", {
    slug: props.entityType,
    projectId: props.projectId || null,
    service: props.service || null,
  });
};

const save = async () => {
  if (!props.isDynamicObject || !props.entityType || !props.propertyMeta?.slug) {
    message.error("Missing required data for record type update");
    return;
  }

  if (selectedRelatedObjects.value.length === 0) {
    message.error("Please select at least one object");
    return;
  }

  try {
    isSaving.value = true;

    // Format related_objects as expected by the API
    const objects = store.getters["objects/objects"] || [];
    const relatedObjects = selectedRelatedObjects.value.map((objectId) => {
      const obj = objects.find((o) => o.id === objectId);
      return {
        object_id: objectId,
        object_type: obj?.slug || objectId,
      };
    });

    const payload = {
      ...props.propertyMeta,
      related_objects: relatedObjects,
      relationship_mode: normalizeRelationshipMode(relationshipMode.value),
      created_by_id: Number(localStorage.getItem("userId")) || 1,
    };

    const response = await CRMService.updateSchemaObjectAttribute(
      props.entityType,
      props.propertyMeta.slug,
      payload,
      props.service,
    );

    if (response.success) {
      await refreshSchemaStore();
      message.success("Related objects updated successfully");

      emit("property-updated", {
        propertyMeta: props.propertyMeta,
        newName: props.propertyMeta.name,
        response: response,
        isRecordTypeChange: true,
      });

      exitEditMode();
      emit("close");
    } else {
      message.error(response.error || "Failed to update related objects");
    }
  } catch (error) {
    console.error("Error updating related objects:", error);
    message.error("Failed to update related objects");
  } finally {
    isSaving.value = false;
  }
};
</script>

<style>
.record_type_edit_dropdown_content {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: var(--dropdown-boxshadow);
  width: fit-content;
  max-height: 400px;
  overflow-y: auto;
}
</style>
